/* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match */
import { ChangeDetectorRef, Directive, EventEmitter, Input, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { View } from 'src/app/modules/core/store/models/workflow.model';
import { ViewComponent, ViewControlType, ViewContainer } from 'src/app/modules/management/store/workflow';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { Document, DocumentSchema, FieldDataType, SchemaField } from '../../store/models/document.model';
import { DynamicField } from './dynamic-field';
import { DataTypeMapService } from './field-type-map.service';

@Directive({
    selector: '[topoDynamicForm2]',
    standalone: false
})
export class DynamicFieldDirective2 implements OnInit, OnDestroy {
  formView$: BehaviorSubject<View> = new BehaviorSubject(null);
  schema$: BehaviorSubject<DocumentSchema> = new BehaviorSubject(null);
  dataset$: BehaviorSubject<Dataset> = new BehaviorSubject(null);
  layerUId$: BehaviorSubject<string> = new BehaviorSubject(null);
  document$: BehaviorSubject<Document> = new BehaviorSubject(null);
  isEdit$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  viewContainer$: BehaviorSubject<ViewContainer> = new BehaviorSubject(null);
  destroy$ = new Subject<void>();
  prevView: View = null;
  prevViewControlContainer: ViewContainer = null;

  @Input() controlCollection: DynamicField[];

  @Input() set formView(value: View) {
    this.formView$.next(value);
  }

  @Input() set schema(value: DocumentSchema) {
    this.schema$.next(value);
  }

  @Input() set dataset(value: Dataset) {
    this.dataset$.next(value);
  }

  @Input() set layerUId(value: string) {
    this.layerUId$.next(value);
  }

  @Input() set document(value: Document) {
    this.document$.next(value);
  }

  @Input() set isEdit(value: boolean) {
    this.isEdit$.next(value);
  }

  @Input() set viewContainer(value: ViewContainer) {
    this.viewContainer$.next(value);
  }

  @Input() configureControl: (control: DynamicField) => void;

  @Output() valueChanged = new EventEmitter<DynamicField>();

  constructor(private view: ViewContainerRef, private fieldComponentMap: DataTypeMapService, private changeDetector: ChangeDetectorRef) {}

  ngOnInit() {
    combineLatest([this.dataset$, this.layerUId$, this.document$, this.isEdit$, this.formView$, this.viewContainer$])
      .pipe(
        filter(([dataset, layerUId, document, isEdit, formView, viewControlContainer]) => {
          return !!dataset && !!document && !!formView && !!viewControlContainer;
        }),
        tap(([dataset, layerUId, document, isEdit, formView, viewControlContainer]) => {
          if (formView !== this.prevView || viewControlContainer !== this.prevViewControlContainer) {
            this.createViewControlsFromViewDefinition(formView, viewControlContainer, dataset, layerUId, document, isEdit);
            this.prevView = formView;
            this.prevViewControlContainer = viewControlContainer;
          }

          this.updateControlValues(formView, dataset, layerUId, document, isEdit);

          this.changeDetector.markForCheck();
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.clearControlCollection();
    this.destroy$.next();
    this.destroy$.complete();
  }

  createViewControlsFromViewDefinition(
    formView: View,
    viewControlContainer: ViewContainer,
    dataset: Dataset,
    layerUId: string,
    document: Document,
    isEdit: boolean
  ): void {
    // init the control collection. this is used to expose
    // the form controls to the parent component
    this.clearControlCollection();

    for (const viewComponent of viewControlContainer?.controls) {
      if (this.canShowField(isEdit, viewComponent, dataset.schema)) {
        this.createViewControl(viewComponent, dataset.schema);
      }
    }
  }

  canShowField(isEdit: boolean, viewComponent: ViewComponent, schema: DocumentSchema): boolean {
    // if in edit mode, do not show auto-number fields. these should be read-only
    if (isEdit && viewComponent.controlType === ViewControlType.DatasetField) {
      var schemaField = schema.fields.find(f => f.uId === viewComponent.dataSetFieldUId);
      if (schemaField?.dataType === FieldDataType.AutoIncrement) {
        return false;
      }
    }

    return true;
  }

  createViewControl(viewComponent: ViewComponent, schema: DocumentSchema): void {
    let componentType: any;
    let field: SchemaField = null;

    if (viewComponent.controlType === ViewControlType.DatasetField) {
      field = schema?.fields?.find(f => f.uId === viewComponent.dataSetFieldUId);

      const dataType = field?.dataType;

      if (!dataType) {
        return;
      }

      componentType = this.fieldComponentMap.getComponentByDataType(dataType);
    } else {
      if (viewComponent.controlType == ViewControlType.Custom) {
        componentType = this.fieldComponentMap.getComponentByCustomViewControlType(viewComponent.configuration?.customControlType);
      } else {
        componentType = this.fieldComponentMap.getComponentByViewControlType(viewComponent.controlType);
      }
    }

    if (!componentType) {
      return;
    }

    // create the component
    const component: any = this.view.createComponent(componentType);
    const control = component.instance as DynamicField;
    control.viewComponent = viewComponent;
    control.field = field;
    control.displayName = field?.displayName;

    control.valueChanged
      .pipe(
        takeUntil(this.destroy$),
        tap((field: DynamicField) => {
          this.valueChanged.emit(field);
        })
      )
      .subscribe();

    if (this.configureControl) {
      this.configureControl(control);
    }

    this.controlCollection.push(control);
  }

  updateControlValues(formView: View, dataset: Dataset, layerUId: string, document: Document, isEdit: boolean): void {
    for (const control of this.controlCollection) {
      control.document = document;
      control.isEdit = isEdit;
      control.layerUId = layerUId;
      control.schema = dataset.schema;
      control.dataset = dataset;

      if (control.tpOnContentUpdated) {
        control.tpOnContentUpdated();
      }
    }
  }

  clearControlCollection(): void {
    if (!!this.controlCollection && this.controlCollection.length > 0) {
      this.controlCollection.splice(0, this.controlCollection.length);
    }

    if (!!this.view && this.view.length > 0) {
      for (let i = 0; i < this.view.length; i++) {
        const ref = this.view.get(i);
        ref.destroy();
      }
    }

    this.view.clear();
  }

  onFieldValueChanged(): void {}
}
